import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';
import { Button } from '../ui/Button';

export interface TimeRange {
  label: string;
  value: string;
  days: number;
  customStart?: string;
  customEnd?: string;
}

export const TIME_RANGES: TimeRange[] = [
  { label: 'Today', value: 'today', days: 1 },
  { label: 'This Week', value: 'week', days: 7 },
  { label: 'This Month', value: 'month', days: 30 },
  { label: 'This Quarter', value: 'quarter', days: 90 },
  { label: 'This Year', value: 'year', days: 365 },
];

export interface TimeRangeSelectorProps {
  selectedRange: TimeRange;
  onRangeChange: (range: TimeRange) => void;
  onCustomRangeChange?: (start: string, end: string) => void;
  customStartDate?: string;
  customEndDate?: string;
  showComparison?: boolean;
  comparisonEnabled?: boolean;
  onComparisonToggle?: (enabled: boolean) => void;
  className?: string;
}

export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  selectedRange,
  onRangeChange,
  onCustomRangeChange,
  customStartDate,
  customEndDate,
  showComparison = true,
  comparisonEnabled = false,
  onComparisonToggle,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(customStartDate || '');
  const [tempEndDate, setTempEndDate] = useState(customEndDate || '');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCustomPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePresetSelect = (range: TimeRange) => {
    onRangeChange(range);
    setIsOpen(false);
    setShowCustomPicker(false);
  };

  const handleCustomRangeApply = () => {
    if (tempStartDate && tempEndDate && onCustomRangeChange) {
      if (new Date(tempStartDate) <= new Date(tempEndDate)) {
        onCustomRangeChange(tempStartDate, tempEndDate);
        setIsOpen(false);
        setShowCustomPicker(false);
      }
    }
  };

  const getDateRangeForPreset = (range: TimeRange): { start: string; end: string } => {
    const end = new Date();
    const start = new Date();
    
    switch (range.value) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'week':
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start.setMonth(start.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(start.getMonth() - 3);
        break;
      case 'year':
        start.setFullYear(start.getFullYear() - 1);
        break;
    }
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="flex items-center gap-2">
        {/* Preset Buttons */}
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
          {TIME_RANGES.map((range) => (
            <button
              key={range.value}
              onClick={() => handlePresetSelect(range)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                selectedRange.value === range.value
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>

        {/* Custom Range Button */}
        <Button
          variant="secondary"
          onClick={() => {
            setIsOpen(!isOpen);
            setShowCustomPicker(true);
            if (customStartDate && customEndDate) {
              setTempStartDate(customStartDate);
              setTempEndDate(customEndDate);
            } else {
              const today = new Date();
              const lastMonth = new Date();
              lastMonth.setMonth(lastMonth.getMonth() - 1);
              setTempStartDate(lastMonth.toISOString().split('T')[0]);
              setTempEndDate(today.toISOString().split('T')[0]);
            }
          }}
          className="flex items-center gap-2"
        >
          <Calendar className="h-4 w-4" />
          Custom
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </Button>

        {/* Comparison Toggle */}
        {showComparison && onComparisonToggle && (
          <Button
            variant={comparisonEnabled ? 'primary' : 'secondary'}
            onClick={() => onComparisonToggle(!comparisonEnabled)}
            className="flex items-center gap-2"
          >
            <span className="text-sm">Compare</span>
          </Button>
        )}
      </div>

      {/* Custom Date Picker Dropdown */}
      {isOpen && showCustomPicker && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Custom Date Range
            </h3>
            <button
              onClick={() => {
                setIsOpen(false);
                setShowCustomPicker(false);
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleCustomRangeApply}
                className="flex-1"
                disabled={!tempStartDate || !tempEndDate || new Date(tempStartDate) > new Date(tempEndDate)}
              >
                Apply
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsOpen(false);
                  setShowCustomPicker(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
